import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Check, Lock, ShieldAlert, UserPlus } from 'lucide-react';
import { McpKeyIdentityContext } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useMcpAvailableTools, useMcpKeyMutations } from '~/hooks/use-mcp-keys';
import { useUsersList, useUserRoles, useUserMutations } from '~/hooks/use-users';
import {
	EMPTY_MCP_KEY,
	EMPTY_SERVICE_USER,
	FRONTEND_TOOLS,
	destructiveViolations,
	mcpKeySchema,
	missingRolePermissions,
	sensitiveWaivers,
	type McpKeyFormValues,
	type McpRiskLevel
} from '~/lib/schemas/mcp-key';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Null = mint a new key; a value = edit name/grants of an existing key (identity is immutable). */
	initial: McpKeyFormValues | null;
	/** Called with the one-time token after a successful mint. */
	onMinted: (token: string, keyName: string) => void;
}

const RISK_ORDER: McpRiskLevel[] = ['Safe', 'Sensitive', 'Destructive'];

/**
 * Mint / edit an MCP key. Streamlines the whole mint-mcp-key.ps1 flow: pick or create the
 * least-privilege backing service user, pick tool grants per risk level, and get the one-time
 * token — with the script's safety rails enforced in the UI (Destructive ⇒ approval locked ON,
 * Sensitive without approval ⇒ warning) and a live authority check against the backing role.
 */
export function McpKeyMintDialog({ open, onOpenChange, initial, onMinted }: Props) {
	const { t } = useTranslation();
	const { create, update } = useMcpKeyMutations();
	const { create: createUser } = useUserMutations();
	const { data: adminTools = [] } = useMcpAvailableTools();
	const { data: users = [] } = useUsersList();
	const { data: roles = [] } = useUserRoles();
	const [submitting, setSubmitting] = useState(false);

	const form = useForm<McpKeyFormValues>({
		resolver: zodResolver(mcpKeySchema) as Resolver<McpKeyFormValues>,
		defaultValues: initial ?? EMPTY_MCP_KEY,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_MCP_KEY);
	}, [open, initial, reset]);

	const context = watch('context');
	const backingMode = watch('backingMode');
	const backingUserId = watch('backingUserId');
	const serviceUser = watch('serviceUser');
	const grants = watch('tools');

	const toolsMeta = context === McpKeyIdentityContext.Admin ? adminTools : FRONTEND_TOOLS;
	const riskByTool = useMemo(() => {
		const map: Record<string, string | undefined> = {};
		for (const meta of toolsMeta) if (meta.name) map[meta.name] = meta.riskLevel;
		return map;
	}, [toolsMeta]);

	const grantedNames = grants.map(g => g.toolName);
	const violations = destructiveViolations(grants, riskByTool);
	const waivers = sensitiveWaivers(grants, riskByTool);

	// Authority check: the backing role's permissions are the key's ceiling (docs/ai/02b §3).
	const backingRoleId =
		context !== McpKeyIdentityContext.Admin
			? null
			: backingMode === 'create'
				? (serviceUser?.rid ?? null)
				: (users.find(u => u.id === backingUserId)?.rid ?? null);
	const backingRole = roles.find(r => r.id === backingRoleId) ?? null;
	const missingPerms =
		context === McpKeyIdentityContext.Admin
			? missingRolePermissions(grantedNames, toolsMeta, backingRole as Record<string, unknown> | null)
			: [];

	const toggleGrant = (toolName: string) => {
		const next = grants.some(g => g.toolName === toolName)
			? grants.filter(g => g.toolName !== toolName)
			: [...grants, { toolName, requiresApproval: true }];
		setValue('tools', next, { shouldDirty: true, shouldValidate: true });
	};

	const setApproval = (toolName: string, requiresApproval: boolean) => {
		setValue(
			'tools',
			grants.map(g => (g.toolName === toolName ? { ...g, requiresApproval } : g)),
			{ shouldDirty: true }
		);
	};

	const switchContext = (next: McpKeyIdentityContext.Admin | McpKeyIdentityContext.FrontEnd) => {
		setValue('context', next, { shouldDirty: true });
		// Tool registries never overlap across contexts — grants can't carry over.
		setValue('tools', [], { shouldDirty: true });
	};

	const onSubmit = handleSubmit(async values => {
		setSubmitting(true);
		try {
			if (values.id) {
				await update.mutateAsync({ form: values, riskByTool });
				toast.success(t('common.save'));
			} else {
				let payload = values;
				if (values.context === McpKeyIdentityContext.Admin && values.backingMode === 'create' && values.serviceUser) {
					// Streamlined flow: create the dedicated service user first, then bind the key to it.
					const su = values.serviceUser;
					const created = await createUser.mutateAsync({
						rid: su.rid,
						eml: su.eml,
						fn: su.fn || null,
						ln: su.ln || null,
						iv: true,
						si: 0,
						psw: su.psw,
						leid: null
					});
					if (!created?.id) throw new Error(t('system.mcpKeys.serviceUserFailed'));
					toast.success(t('system.mcpKeys.serviceUserCreated', { email: su.eml }));
					payload = { ...values, backingMode: 'existing', backingUserId: created.id, serviceUser: null };
				}
				const minted = await create.mutateAsync({ form: payload, riskByTool });
				if (minted.token) onMinted(minted.token, values.name.trim());
			}
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		} finally {
			setSubmitting(false);
		}
	});

	const approvalCount = grants.filter(g => g.requiresApproval || riskByTool[g.toolName] === 'Destructive').length;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="flex max-h-[88vh] flex-col sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>{isEdit ? t('system.mcpKeys.editTitle') : t('system.mcpKeys.addTitle')}</DialogTitle>
					<DialogDescription>{t('system.mcpKeys.formSubtitle')}</DialogDescription>
				</DialogHeader>
				{/* Header and footer stay pinned; only the form body scrolls (UX: actions always visible). */}
				<form onSubmit={onSubmit} noValidate className="flex min-h-0 flex-1 flex-col gap-3">
					<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
							<FormField label={t('system.mcpKeys.columns.name')} error={formState.errors.name?.message}>
								<Input {...register('name')} maxLength={200} aria-invalid={Boolean(formState.errors.name)} autoFocus />
							</FormField>
							<FormField label={t('system.mcpKeys.context')}>
								<Select
									value={String(context)}
									onValueChange={v =>
										switchContext(Number(v) === McpKeyIdentityContext.Admin ? McpKeyIdentityContext.Admin : McpKeyIdentityContext.FrontEnd)
									}
									disabled={isEdit}>
									<SelectTrigger aria-label={t('system.mcpKeys.context')}>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value={String(McpKeyIdentityContext.Admin)}>{t('system.mcpKeys.contextAdmin')}</SelectItem>
										<SelectItem value={String(McpKeyIdentityContext.FrontEnd)}>{t('system.mcpKeys.contextFrontEnd')}</SelectItem>
									</SelectContent>
								</Select>
							</FormField>
						</div>

						{/* Backing identity — immutable after mint */}
						{!isEdit && context === McpKeyIdentityContext.Admin && (
							<div className="flex flex-col gap-3 rounded-md border border-border p-3">
								<div className="flex items-center justify-between">
									<span className="text-xs font-medium text-foreground-subtle">{t('system.mcpKeys.backingIdentity')}</span>
									<Button
										type="button"
										variant={backingMode === 'create' ? 'secondary' : 'outline'}
										size="sm"
										onClick={() => {
											const next = backingMode === 'create' ? 'existing' : 'create';
											setValue('backingMode', next, { shouldDirty: true });
											setValue('serviceUser', next === 'create' ? EMPTY_SERVICE_USER : null, { shouldValidate: true });
										}}>
										<UserPlus size={14} className="mr-1.5" />
										{backingMode === 'create' ? t('system.mcpKeys.backingPickExisting') : t('system.mcpKeys.backingCreateNew')}
									</Button>
								</div>
								<p className="text-xs text-foreground-muted">{t('system.mcpKeys.backingHint')}</p>

								{backingMode === 'existing' ? (
									<FormField label={t('system.mcpKeys.backingUser')} error={formState.errors.backingUserId?.message}>
										<Select
											value={backingUserId ? String(backingUserId) : ''}
											onValueChange={v => setValue('backingUserId', Number(v), { shouldDirty: true, shouldValidate: true })}>
											<SelectTrigger aria-label={t('system.mcpKeys.backingUser')}>
												<SelectValue placeholder={t('system.mcpKeys.backingUserPlaceholder')} />
											</SelectTrigger>
											<SelectContent>
												{users.map(u => (
													<SelectItem key={u.id} value={String(u.id)}>
														{u.eml}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</FormField>
								) : (
									<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
										<FormField label={t('system.mcpKeys.serviceUserEmail')} error={formState.errors.serviceUser?.eml?.message}>
											<Input type="email" autoComplete="off" {...register('serviceUser.eml')} />
										</FormField>
										<FormField label={t('system.mcpKeys.serviceUserPassword')} error={formState.errors.serviceUser?.psw?.message}>
											<Input type="password" autoComplete="new-password" {...register('serviceUser.psw')} />
										</FormField>
										<FormField label={t('system.mcpKeys.serviceUserFirstName')} error={formState.errors.serviceUser?.fn?.message}>
											<Input {...register('serviceUser.fn')} />
										</FormField>
										<FormField label={t('system.mcpKeys.serviceUserLastName')} error={formState.errors.serviceUser?.ln?.message}>
											<Input {...register('serviceUser.ln')} />
										</FormField>
										<FormField label={t('system.mcpKeys.serviceUserRole')} error={formState.errors.serviceUser?.rid?.message}>
											<Select
												value={serviceUser?.rid ? String(serviceUser.rid) : ''}
												onValueChange={v => setValue('serviceUser.rid', Number(v), { shouldDirty: true, shouldValidate: true })}>
												<SelectTrigger aria-label={t('system.mcpKeys.serviceUserRole')}>
													<SelectValue placeholder={t('system.mcpKeys.serviceUserRolePlaceholder')} />
												</SelectTrigger>
												<SelectContent>
													{roles.map(r => (
														<SelectItem key={r.id} value={String(r.id)}>
															{r.nm}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</FormField>
									</div>
								)}
							</div>
						)}

						{!isEdit && context === McpKeyIdentityContext.FrontEnd && (
							<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
								<FormField label={t('system.mcpKeys.backingCustomer')} error={formState.errors.backingCustomerId?.message}>
									<Input
										type="number"
										{...register('backingCustomerId', { setValueAs: v => (v === '' || v === null ? null : Number(v)) })}
									/>
								</FormField>
								<FormField label={t('system.mcpKeys.siteId')}>
									<Input type="number" {...register('siteId', { setValueAs: v => (v === '' || v === null ? null : Number(v)) })} />
								</FormField>
							</div>
						)}

						{isEdit && (
							<p className="rounded-md bg-muted px-3 py-2 text-xs text-foreground-muted">
								{t('system.mcpKeys.identityFrozen', {
									context: context === McpKeyIdentityContext.Admin ? t('system.mcpKeys.contextAdmin') : t('system.mcpKeys.contextFrontEnd'),
									backing:
										context === McpKeyIdentityContext.Admin
											? (users.find(u => u.id === backingUserId)?.eml ?? `#${backingUserId ?? '—'}`)
											: `#${watch('backingCustomerId') ?? '—'}`
								})}
							</p>
						)}

						{/* Tool grants, grouped by risk */}
						<div className="flex flex-col gap-2">
							<div className="flex items-baseline justify-between">
								<span className="text-xs font-medium text-foreground-subtle">{t('system.mcpKeys.grants')}</span>
								<span className="text-xs tabular-nums text-foreground-muted">
									{t('system.mcpKeys.grantsSummary', { granted: grants.length, gated: approvalCount })}
								</span>
							</div>
							{formState.errors.tools?.message && <span className="text-xs text-destructive">{t(formState.errors.tools.message)}</span>}
							{RISK_ORDER.map(risk => {
								const group = toolsMeta.filter(m => m.riskLevel === risk);
								if (group.length === 0) return null;
								return (
									<div key={risk} className="rounded-md border border-border">
										<div className="border-b border-border bg-muted/50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-muted">
											{t(`system.mcpKeys.risk${risk}`)}
										</div>
										<ul className="divide-y divide-border">
											{group.map(meta => {
												const granted = grants.find(g => g.toolName === meta.name);
												const destructive = meta.riskLevel === 'Destructive';
												return (
													<li key={meta.name} className="flex items-center gap-3 px-3 py-2">
														<button
															type="button"
															onClick={() => meta.name && toggleGrant(meta.name)}
															aria-pressed={Boolean(granted)}
															aria-label={meta.name}
															className={`flex size-4.5 shrink-0 items-center justify-center rounded border ${
																granted ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background'
															}`}>
															{granted && <Check size={12} strokeWidth={3} />}
														</button>
														<div className="min-w-0 flex-1">
															<button
																type="button"
																className="block max-w-full truncate text-left font-mono text-xs text-foreground"
																title={meta.description ?? ''}
																onClick={() => meta.name && toggleGrant(meta.name)}>
																{meta.name}
															</button>
															{(meta.requiredPermissions?.length ?? 0) > 0 && (
																<span className="text-[10px] text-foreground-muted">
																	{t('system.mcpKeys.requiresPerms', { perms: meta.requiredPermissions?.join(', ') })}
																</span>
															)}
														</div>
														{granted && (
															<label className="flex shrink-0 items-center gap-1.5 text-[11px] text-foreground-muted">
																{destructive && <Lock size={11} aria-hidden />}
																{t('system.mcpKeys.approval')}
																<Switch
																	checked={destructive ? true : granted.requiresApproval}
																	disabled={destructive}
																	onCheckedChange={v => meta.name && setApproval(meta.name, v)}
																	aria-label={t('system.mcpKeys.approvalFor', { tool: meta.name })}
																/>
															</label>
														)}
													</li>
												);
											})}
										</ul>
									</div>
								);
							})}
						</div>

						{/* Safety + authority feedback */}
						{waivers.length > 0 && (
							<div className="flex items-start gap-2 rounded-md border border-warning bg-warning/10 px-3 py-2 text-xs text-warning-fg">
								<AlertTriangle size={14} className="mt-0.5 shrink-0" />
								<span>{t('system.mcpKeys.sensitiveWaiverWarning', { tools: waivers.join(', ') })}</span>
							</div>
						)}
						{missingPerms.length > 0 && (
							<div className="flex items-start gap-2 rounded-md border border-destructive/60 bg-destructive/10 px-3 py-2 text-xs text-destructive">
								<ShieldAlert size={14} className="mt-0.5 shrink-0" />
								<span>{t('system.mcpKeys.missingPermsWarning', { role: backingRole?.nm ?? '', perms: missingPerms.join(', ') })}</span>
							</div>
						)}
					</div>

					<DialogFooter className="border-t border-border pt-3">
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={submitting || violations.length > 0}>
							{submitting ? t('common.saving') : isEdit ? t('common.save') : t('system.mcpKeys.mint')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
